/* globals zoomSdk */
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { useSelector, useDispatch } from 'react-redux'
import { sampleBase64Image } from '../__data__/sampleBase64Image'
import NewPresets from './NewPresets'
import NewPresetsMobile from './NewPresetsMobile'
import NewInputBox from './NewInputBox'
import NewGeneratedImages from './NewGeneratedImages'
import NotificationMessage from './NotificationMessage'
import {
  generateTextToImages,
  resetAddImage,
  resetTextToImage,
  setTextToImageError,
  setTextToImages,
} from '../vb/actions'
import {
  selectTextToImagesError,
  selectTextToImagesLoading,
  selectTextToImagesRequestId,
  selectTextToImagesSuccess,
} from '../vb/reducer'
import { selectZaConfig, selectZaConfigError } from '../root/reducer'
import useLightModePreference from '../util/useLightModePreference'
import {
  selectIsResultCleared,
  selectErrorMessage,
  selectQuery,
  selectSpace,
} from './reducer'
import * as CONSTANTS from './constants'
import { ERROR_MESSAGE_PROMPT_ADJUSTED } from './constants'
import { setIsNotificationVisible } from './actions'
import GeneralErrorScreen from './GeneralErrorScreen'
import {
  logAIVirtualBackgroundDisplayed,
  logGenerateButtonClicked,
} from '../telemetry/telemetry'
import './NewClientSetting.css'
import { initSnowplow } from '../telemetry/telemetry'

const ErrorFallback = () => {
  return <GeneralErrorScreen />
}

const MOCK_TEXT_TO_IMAGE_IMAGES = [
  {
    id: 'mock-image-1',
    imageData: sampleBase64Image,
  },
  {
    id: 'mock-image-2',
    imageData: sampleBase64Image,
  },
]

const NewClientSetting = () => {
  const { i18n } = useTranslation()
  const [generatedImagesKey, setGeneratedImagesKey] = useState(0) // key to trigger re-render
  const searchParams = new URLSearchParams(window.location.search)
  const isClientSettingPreview = searchParams.get('view') === 'clientSetting'
  const shouldPreviewGeneratedImages = searchParams.get('showImages') === 'true'
  const shouldPreviewLoadingImages =
    searchParams.get('imagesLoading') === 'true'
  const dispatch = useDispatch()
  const textToImagesLoading = useSelector(selectTextToImagesLoading)
  const zaConfigOutput = useSelector(selectZaConfig)
  const isMobileDevice = zaConfigOutput?.product === 'mobile'
  const zaConfigError = useSelector(selectZaConfigError)
  const isLightMode = useLightModePreference()
  const errorMessage = useSelector(selectErrorMessage)
  const textToImageError = useSelector(selectTextToImagesError)
  const textToImageSuccess = useSelector(selectTextToImagesSuccess)
  const isResultCleared = useSelector(selectIsResultCleared)
  const requestId = useSelector(selectTextToImagesRequestId)
  const query = useSelector(selectQuery)
  const selectedSpace = useSelector(selectSpace)
  const queryLength = query?.trim().length || 0
  const hasGenerationInput = Boolean(queryLength || selectedSpace)
  const showGeneratedImages =
    (textToImagesLoading ||
      textToImageSuccess ||
      shouldPreviewGeneratedImages ||
      shouldPreviewLoadingImages) &&
    !errorMessage &&
    !textToImageError &&
    (hasGenerationInput ||
      shouldPreviewGeneratedImages ||
      shouldPreviewLoadingImages) &&
    !isResultCleared

  useEffect(() => {
    if (zaConfigOutput) {
      initSnowplow()
        .then(() => {
          // Log the display event after Snowplow is initialized
          logAIVirtualBackgroundDisplayed()
        })
        .catch((error) => {
          console.error('Client Setting: Snowplow initialization failed', error)
        })
    }
  }, [zaConfigOutput])

  useEffect(() => {
    if (
      !hasGenerationInput &&
      !shouldPreviewGeneratedImages &&
      !shouldPreviewLoadingImages
    ) {
      dispatch(resetTextToImage())
      dispatch(setIsNotificationVisible(false))
      dispatch(resetAddImage())
    }
  }, [
    hasGenerationInput,
    dispatch,
    shouldPreviewGeneratedImages,
    shouldPreviewLoadingImages,
  ])

  useEffect(() => {
    if (!isClientSettingPreview || !shouldPreviewGeneratedImages) {
      return
    }

    dispatch(
      setTextToImages(
        MOCK_TEXT_TO_IMAGE_IMAGES.map((image, index) => ({
          ...image,
          id: `${image.id}-${index}`,
        }))
      )
    )
  }, [dispatch, isClientSettingPreview, shouldPreviewGeneratedImages])

  useEffect(() => {
    if (!isClientSettingPreview || !shouldPreviewLoadingImages) {
      return
    }

    dispatch(
      generateTextToImages.pending('preview-loading', {
        query: 'preview-loading',
      })
    )
  }, [dispatch, isClientSettingPreview, shouldPreviewLoadingImages])

  useEffect(() => {
    const zmOnTextToImage = (event) => {
      /*
      console.info('NewClientSetting: zmOnTextToImage event', {
        code: event.code,
        hasImages: Boolean(event.images?.length),
        imagesCount: event.images?.length || 0,
        requestId: event.requestId,
      })
      */

      if (event.requestId !== requestId) return
      const isPromptAdjustedError = event.code === 201003

      if (isPromptAdjustedError) {
        dispatch(setTextToImageError(ERROR_MESSAGE_PROMPT_ADJUSTED))
        logGenerateButtonClicked({
          errorReason: ERROR_MESSAGE_PROMPT_ADJUSTED,
        })
      } else if (
        event.images?.length > 0 &&
        !errorMessage &&
        !textToImageError
      ) {
        dispatch(setTextToImages(event.images))
      } else {
        const isContentModerationError =
          event.code === 100002 ||
          event.code === 100003 ||
          event.code === 100004

        const errorMessage = isContentModerationError
          ? 'content moderation'
          : CONSTANTS.ERROR_MESSAGE_FAILED_TO_GENERATE

        dispatch(setTextToImageError(errorMessage))
        logGenerateButtonClicked({ errorReason: errorMessage })
      }
    }

    if (zaConfigOutput) {
      window.zoomSdk.addEventListener('zmOnTextToImage', zmOnTextToImage)
    }

    if (zaConfigOutput) {
      return () => {
        window.zoomSdk.removeEventListener('zmOnTextToImage', zmOnTextToImage)
      }
    }
  }, [zaConfigOutput, errorMessage, textToImageError, requestId, dispatch])

  useEffect(() => {
    const getLanguage = async () => {
      const { language } = await zoomSdk.zmGetLanguage()
      i18n.changeLanguage(language === 'pt-pt' ? 'pt' : language)
    }

    if (zaConfigOutput) {
      getLanguage()
    }
  }, [zaConfigOutput, i18n])

  // When press 'tab' on the last element on the landing page, it should go back to the first element, and vice versa.
  useEffect(() => {
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        const firstElement = document.querySelector('input')
        const lastElement =
          document.querySelector('.lastArtisticStyle') ||
          document.querySelector('.lastGeneratedImage') ||
          document.querySelector('.lastLoadingImage')

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement?.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement?.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => {
      document.removeEventListener('keydown', handleTabKey)
    }
  }, [])

  useEffect(() => {
    const handleError = async () => {
      if (zaConfigError) {
        logAIVirtualBackgroundDisplayed({ errorReason: zaConfigError })
      }
    }
    handleError()
  }, [zaConfigError])

  if (zaConfigError && !isClientSettingPreview) {
    return <GeneralErrorScreen />
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div
        className={`new-client-setting ${!isLightMode ? 'dark-mode' : ''} ${
          isMobileDevice ? 'mobile' : ''
        } ${!textToImageError ? 'no-error' : ''}`}
      >
        {isMobileDevice ? <NewPresetsMobile /> : <NewPresets />}
        <NewInputBox setGeneratedImagesKey={setGeneratedImagesKey} />
        <div className="new-client-setting-content">
          {showGeneratedImages ? (
            <NewGeneratedImages key={generatedImagesKey} />
          ) : null}
        </div>
        <NotificationMessage />
      </div>
    </ErrorBoundary>
  )
}

export default NewClientSetting
